import { useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface TicketReplyBoxProps {
  onReply: (text: string) => void;
  isReplying: boolean;
  /** Clears the draft once the reply actually lands, not on click — a failed
   * send must not lose what the agent typed. */
  isReplySuccess: boolean;
  /** Translated message for the most recent reply failure, if any. */
  replyError: string | null;
}

/**
 * The composer at the foot of the chat: the agent's reply goes to the user
 * in Telegram. Enter sends, Shift+Enter breaks the line. Rendered only for
 * agents holding SUPPORT_MANAGE.
 */
export function TicketReplyBox({
  onReply,
  isReplying,
  isReplySuccess,
  replyError,
}: TicketReplyBoxProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');

  // Only clear the draft once the mutation actually succeeds; a network
  // error must leave the agent's text in place so nothing is lost.
  useEffect(() => {
    if (isReplySuccess) setText('');
  }, [isReplySuccess]);

  const canSend = !isReplying && text.trim().length > 0;

  const handleSend = () => {
    if (!canSend) return;
    onReply(text.trim());
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // `isComposing`: Enter that confirms an IME candidate is not a send.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <footer className="chat-composer">
      <div className="chat-composer-box">
        <textarea
          className="chat-composer-input"
          rows={1}
          aria-label={t('dashboard.support.replyPlaceholder')}
          placeholder={t('dashboard.support.replyPlaceholder')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isReplying}
          maxLength={4000}
        />
        <button
          type="button"
          className="chat-send-btn"
          onClick={handleSend}
          disabled={!canSend}
        >
          <span className="material-symbols-outlined" aria-hidden="true">send</span>
          {t('dashboard.support.reply')}
        </button>
      </div>
      {replyError ? (
        <p className="chat-composer-error" role="alert">{replyError}</p>
      ) : (
        <p className="chat-composer-hint">{t('dashboard.support.composerHint')}</p>
      )}
    </footer>
  );
}

export default TicketReplyBox;
